/**
 * Keeps a field's model in sync with what the browser actually put in the input.
 *
 * Figma `66:47545` calls this out explicitly: submit buttons are gated on field
 * state, and password managers / browser autofill do not reliably fire `input`.
 * Without this a fully filled form can sit behind a permanently disabled button.
 *
 * Three layers, because no single one covers every browser:
 *   1. read the DOM after mount — catches Safari and instant Chrome autofill
 *   2. `animationstart` on `:-webkit-autofill` — the only reliable signal for
 *      late autofill in Chrome/Safari (the rule lives in `fair-auth-modal.css`)
 *   3. a short poll — Firefox has no `:-webkit-autofill`
 */
import { onBeforeUnmount, onMounted, type Ref } from 'vue'

/** Must match the keyframes name used by the `:-webkit-autofill` rule in CSS. */
export const AUTOFILL_ANIMATION_NAME = 'fair-autofill-start'

const POLL_INTERVAL_MS = 200
const POLL_DURATION_MS = 2000

export function useAutofillSync(
  elementRef: Ref<HTMLInputElement | null | undefined>,
  modelRef: Ref<string>,
) {
  let pollTimer: ReturnType<typeof setInterval> | null = null
  let stopTimer: ReturnType<typeof setTimeout> | null = null
  let frame: number | null = null

  function sync() {
    const el = elementRef.value
    if (!el) return
    if (el.value !== modelRef.value) {
      modelRef.value = el.value
    }
  }

  function handleAnimationStart(event: AnimationEvent) {
    if (event.animationName === AUTOFILL_ANIMATION_NAME) sync()
  }

  function stopPolling() {
    if (pollTimer !== null) {
      clearInterval(pollTimer)
      pollTimer = null
    }
    if (stopTimer !== null) {
      clearTimeout(stopTimer)
      stopTimer = null
    }
  }

  onMounted(() => {
    // Two frames: one for the browser to paint, one for autofill to land.
    frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(sync)
    })

    elementRef.value?.addEventListener('animationstart', handleAnimationStart)

    pollTimer = setInterval(sync, POLL_INTERVAL_MS)
    stopTimer = setTimeout(stopPolling, POLL_DURATION_MS)
  })

  onBeforeUnmount(() => {
    if (frame !== null) cancelAnimationFrame(frame)
    elementRef.value?.removeEventListener('animationstart', handleAnimationStart)
    stopPolling()
  })

  return { sync }
}
